/**
 * Fale Conosco — Snap entre seções via Lenis (1 wheel/keystroke = 1 seção).
 * Mesmo padrão do product-detail-desk: o rodapé é uma snap section dentro do
 * próprio container (#talk-to-us), então o handler de wheel/touch sempre
 * preventDefault e navega via scrollToCurrent — sem zona morta entre snap
 * chain e footer.
 *
 * Touch (mobile): preventDefault em touchmove para a página não rolar
 * nativamente durante o arrasto. O snap dispara assim que o dedo passa do
 * threshold — mesmo padrão do empreendimento mobile — para o gesto virar
 * "puxa → snap" em vez de "puxa → rola livre → snap corrige".
 */

require('./home-mobile/modules/contato-dropdown');

const MOBILE_MAX_WIDTH = 768;
const WHEEL_THRESHOLD = 15;
const TOUCH_THRESHOLD = 70;
const SNAP_LOCK_MS = 1300;
const NO_SNAP_SELECTOR = 'input, textarea, select, .leaflet-container';

function isMobile() {
    return window.innerWidth <= MOBILE_MAX_WIDTH;
}

// expo.inOut — mesmo easing da navegação do empreendimento mobile.
const expoInOut = (t) =>
    t === 0
        ? 0
        : t === 1
        ? 1
        : t < 0.5
        ? Math.pow(2, 20 * t - 10) / 2
        : (2 - Math.pow(2, -20 * t + 10)) / 2;

// ──────────────────────────────────────────────────────────────────────
// SNAP SCROLL
// ──────────────────────────────────────────────────────────────────────
function initSnapScroll(root) {
    if (typeof Lenis === 'undefined') {
        console.warn('[talk-to-us] Lenis not available');
        return null;
    }

    let lenis = null;
    let rafId = null;
    let sections = [];
    let currentIndex = 0;
    let isSnapping = false;
    let snapLockTimer = null;
    let lastScrollTime = 0;
    let wheelAccumulator = 0;
    let wheelTimer = null;
    let touchStartY = 0;
    let touchStartX = 0;
    let touchTarget = null;
    let touchTriggered = false;
    let prevSectionCount = 0;

    const indicator = document.getElementById('tt-scroll-indicator');

    function refreshSections() {
        // Mobile: hero / info / form / map / rodape são snap-stops separados.
        // Desktop+tablet: hero+info ficam combinados (#tt-contact).
        const ids = isMobile()
            ? ['tt-hero', 'tt-info', 'tt-form', 'tt-map', 'tt-rodape']
            : ['tt-contact', 'tt-form', 'tt-map', 'tt-rodape'];

        sections = ids
            .map((id) => document.getElementById(id))
            .filter((el) => el && el.offsetParent !== null);
    }

    function syncCurrentIndex() {
        if (!sections.length) return;
        const y = window.scrollY ?? document.documentElement.scrollTop ?? 0;
        const center = y + window.innerHeight / 2;
        let closest = 0;
        let dist = Infinity;
        sections.forEach((s, i) => {
            const rect = s.getBoundingClientRect();
            const top = rect.top + y;
            const sCenter = top + rect.height / 2;
            const d = Math.abs(center - sCenter);
            if (d < dist) {
                dist = d;
                closest = i;
            }
        });
        currentIndex = closest;
    }

    function updateIndicator() {
        if (!indicator) return;
        const onLast = currentIndex === sections.length - 1;
        if (typeof gsap !== 'undefined') {
            gsap.to(indicator, {
                opacity: onLast ? 0 : 1,
                pointerEvents: onLast ? 'none' : 'auto',
                duration: 0.4,
            });
        } else {
            indicator.classList.toggle('is-hidden', onLast);
        }
    }

    function goTo(idx) {
        if (!lenis) return;
        const clamped = Math.max(0, Math.min(idx, sections.length - 1));
        if (clamped === currentIndex && isSnapping) return;
        currentIndex = clamped;
        isSnapping = true;
        updateIndicator();

        lenis.scrollTo(sections[currentIndex], {
            offset: 0,
            duration: 1.2,
            easing: expoInOut,
            lock: true,
            force: true,
            onComplete: () => {
                clearTimeout(snapLockTimer);
                snapLockTimer = setTimeout(() => {
                    isSnapping = false;
                }, 100);
            },
        });

        // Failsafe — caso onComplete não dispare
        clearTimeout(snapLockTimer);
        snapLockTimer = setTimeout(() => {
            isSnapping = false;
        }, SNAP_LOCK_MS);
    }

    function onWheel(e) {
        e.preventDefault();
        const now = Date.now();
        if (isSnapping || now - lastScrollTime < SNAP_LOCK_MS) {
            wheelAccumulator = 0;
            return;
        }
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

        wheelAccumulator += e.deltaY;
        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(() => {
            wheelAccumulator = 0;
        }, 160);

        if (Math.abs(wheelAccumulator) >= WHEEL_THRESHOLD) {
            const dir = wheelAccumulator > 0 ? 1 : -1;
            wheelAccumulator = 0;
            if (dir > 0 && currentIndex < sections.length - 1) {
                lastScrollTime = now;
                goTo(currentIndex + 1);
            } else if (dir < 0 && currentIndex > 0) {
                lastScrollTime = now;
                goTo(currentIndex - 1);
            }
        }
    }

    function onKey(e) {
        const ae = document.activeElement;
        if (ae && ['INPUT', 'TEXTAREA', 'SELECT'].includes(ae.tagName)) return;
        if (['ArrowDown', 'PageDown', 'Space'].includes(e.code)) {
            e.preventDefault();
            goTo(currentIndex + 1);
        } else if (['ArrowUp', 'PageUp'].includes(e.code)) {
            e.preventDefault();
            goTo(currentIndex - 1);
        } else if (e.code === 'Home') {
            e.preventDefault();
            goTo(0);
        } else if (e.code === 'End') {
            e.preventDefault();
            goTo(sections.length - 1);
        }
    }

    function onTouchStart(e) {
        // Targets que precisam manter interação nativa (form, mapa) ficam de
        // fora do snap por toque — touchTarget = null curto-circuita o move.
        if (e.target.closest && e.target.closest(NO_SNAP_SELECTOR)) {
            touchTarget = null;
            return;
        }
        const t = e.touches[0];
        touchStartY = t.clientY;
        touchStartX = t.clientX;
        touchTarget = e.target;
        touchTriggered = false;
    }

    function onTouchMove(e) {
        if (!touchTarget) return;

        // Animação rodando ou já disparou o snap nesse gesto: bloqueia o
        // resto da rolagem nativa e ignora.
        if (isSnapping || touchTriggered) {
            if (e.cancelable) e.preventDefault();
            return;
        }

        const t = e.touches[0];
        const dy = touchStartY - t.clientY;
        const dx = touchStartX - t.clientX;

        // Gesto majoritariamente horizontal: deixa o navegador resolver
        // (não há swiper na página, mas evita capturar tap-and-drag lateral).
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) return;

        // CHAVE DA CORREÇÃO: bloqueia o scroll nativo antes de avaliar o
        // threshold. Sem isso, a página rola livre durante o arrasto e o
        // snap em touchend "corrige" a posição — sensação de travada.
        if (e.cancelable) e.preventDefault();

        const now = Date.now();
        if (now - lastScrollTime < SNAP_LOCK_MS) return;
        if (Math.abs(dy) < TOUCH_THRESHOLD) return;

        if (dy > 0 && currentIndex < sections.length - 1) {
            lastScrollTime = now;
            touchTriggered = true;
            goTo(currentIndex + 1);
        } else if (dy < 0 && currentIndex > 0) {
            lastScrollTime = now;
            touchTriggered = true;
            goTo(currentIndex - 1);
        }
    }

    function onTouchEnd() {
        touchTarget = null;
        touchTriggered = false;
    }

    function onAnchorClick(e) {
        const link = e.target.closest('a[href^="#"], [data-scroll-to]');
        if (!link || !root.contains(link)) return;

        let target = null;
        if (link.dataset.scrollTo) {
            target = sections[parseInt(link.dataset.scrollTo, 10) - 1] || null;
        } else {
            const href = link.getAttribute('href');
            if (!href || href === '#') return;
            target = document.querySelector(href);
        }
        if (!target) return;

        e.preventDefault();
        const idx = sections.indexOf(target);
        if (idx >= 0) {
            goTo(idx);
            return;
        }

        // Alvo fora do snap chain (ex.: link interno) — scroll suave via Lenis
        if (lenis) {
            isSnapping = true;
            lenis.scrollTo(target, {
                duration: 1.2,
                easing: expoInOut,
                onComplete: () => {
                    clearTimeout(snapLockTimer);
                    snapLockTimer = setTimeout(() => {
                        isSnapping = false;
                    }, 100);
                },
            });
            clearTimeout(snapLockTimer);
            snapLockTimer = setTimeout(() => {
                isSnapping = false;
            }, SNAP_LOCK_MS);
        }
    }

    function destroy() {
        if (lenis) {
            lenis.destroy();
            lenis = null;
        }
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
        window.removeEventListener('wheel', onWheel, { passive: false });
        window.removeEventListener('keydown', onKey);
        window.removeEventListener('touchstart', onTouchStart);
        window.removeEventListener('touchmove', onTouchMove, { passive: false });
        window.removeEventListener('touchend', onTouchEnd);
        window.removeEventListener('touchcancel', onTouchEnd);
        document.removeEventListener('click', onAnchorClick);
    }

    function setup() {
        destroy();

        lenis = new Lenis({
            duration: 1.2,
            easing: expoInOut,
            orientation: 'vertical',
            gestureOrientation: 'vertical',
            smoothWheel: false,
            touchMultiplier: 0,
            infinite: false,
            autoResize: true,
        });

        window.lenis = lenis;

        function raf(time) {
            if (!lenis) return;
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        }
        rafId = requestAnimationFrame(raf);

        refreshSections();
        prevSectionCount = sections.length;
        if (!sections.length) return;

        currentIndex = 0;
        lenis.scrollTo(sections[0], { immediate: true });
        updateIndicator();

        window.addEventListener('wheel', onWheel, { passive: false });
        window.addEventListener('keydown', onKey);
        window.addEventListener('touchstart', onTouchStart, { passive: true });
        window.addEventListener('touchmove', onTouchMove, { passive: false });
        window.addEventListener('touchend', onTouchEnd, { passive: true });
        window.addEventListener('touchcancel', onTouchEnd, { passive: true });
        document.addEventListener('click', onAnchorClick);
    }

    let resizeT;
    function onResize() {
        clearTimeout(resizeT);
        resizeT = setTimeout(() => {
            refreshSections();
            if (sections.length !== prevSectionCount) {
                prevSectionCount = sections.length;
                setup();
                return;
            }
            syncCurrentIndex();
            updateIndicator();
        }, 200);
    }

    setup();
    window.addEventListener('resize', onResize);

    // Click no indicator avança 1 seção.
    if (indicator) {
        indicator.addEventListener('click', () => {
            if (currentIndex < sections.length - 1) {
                goTo(currentIndex + 1);
            }
        });
    }

    return {
        goTo,
        getIndex: () => currentIndex,
        destroy,
    };
}

// ──────────────────────────────────────────────────────────────────────
// REVEALS (GSAP)
// ──────────────────────────────────────────────────────────────────────
function initReveals() {
    if (typeof gsap === 'undefined') return;

    const tl = gsap.timeline({ defaults: { ease: 'expo.out', duration: 1.2 } });

    if (document.querySelector('.js-reveal-eyebrow')) {
        tl.to('.js-reveal-eyebrow', { opacity: 1, y: 0, duration: 1.0, delay: 0.1 });
    }
    if (document.querySelector('.js-reveal-title')) {
        tl.to('.js-reveal-title', { opacity: 1, y: 0, duration: 1.4 }, '-=0.85');
    }
    if (document.querySelector('.js-reveal-text')) {
        tl.to('.js-reveal-text', { opacity: 1, y: 0 }, '-=1.0');
    }
    if (document.querySelector('.js-reveal-btn')) {
        tl.to('.js-reveal-btn', { opacity: 1, y: 0 }, '-=0.95');
    }
    if (document.querySelectorAll('.js-reveal-card').length) {
        tl.to(
            '.js-reveal-card',
            { opacity: 1, y: 0, stagger: 0.1, duration: 1.0 },
            '-=1.0'
        );
    }
}

// ──────────────────────────────────────────────────────────────────────
// LEAFLET MAP
// ──────────────────────────────────────────────────────────────────────
function initMap() {
    if (typeof L === 'undefined') return;
    const mapEl = document.getElementById('tt-map__leaflet');
    if (!mapEl) return;

    const lat = parseFloat(document.getElementById('tt-lat')?.value);
    const lng = parseFloat(document.getElementById('tt-lng')?.value);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const VERDE = '#D0E5D2';

    const map = L.map(mapEl, {
        scrollWheelZoom: false,
        zoomControl: false,
        dragging: !L.Browser.mobile,
        touchZoom: true,
        attributionControl: true,
    }).setView([lat, lng], 16);

    L.control.zoom({ position: 'topright' }).addTo(map);
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; OpenStreetMap &copy; CARTO',
        subdomains: 'abcd',
        maxZoom: 19,
    }).addTo(map);

    const icon = L.divIcon({
        className: 'tt-map__marker',
        html:
            `<div style="color:${VERDE};font-size:42px;` +
            `filter:drop-shadow(0 0 8px rgba(0,0,0,0.8));">` +
            `<ion-icon name="location" style="font-size:42px;"></ion-icon>` +
            `</div>`,
        iconSize: [42, 48],
        iconAnchor: [21, 48],
    });

    L.marker([lat, lng], { icon }).addTo(map);

    const refresh = () => map.invalidateSize();
    setTimeout(refresh, 200);
    window.addEventListener('resize', () => clearTimeout(refresh._t), { once: true });
    const obs = new IntersectionObserver(
        (entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) refresh();
            });
        },
        { threshold: 0.1 }
    );
    const section = document.getElementById('tt-map');
    if (section) obs.observe(section);
}

// ──────────────────────────────────────────────────────────────────────
// COPIAR ENDEREÇO
// ──────────────────────────────────────────────────────────────────────
function initCopyAddress() {
    const btn = document.getElementById('tt-copy-btn');
    if (!btn) return;
    btn.addEventListener('click', async () => {
        const address = btn.dataset.address || '';
        if (!address) return;
        try {
            await navigator.clipboard.writeText(address);
            const icon = btn.querySelector('ion-icon');
            if (icon) {
                const orig = icon.getAttribute('name');
                icon.setAttribute('name', 'checkmark-outline');
                setTimeout(() => icon.setAttribute('name', orig || 'copy-outline'), 2000);
            }
        } catch (err) {
            console.error('[talk-to-us] copy failed', err);
        }
    });
}

// ──────────────────────────────────────────────────────────────────────
// BOOT
// ──────────────────────────────────────────────────────────────────────
function boot() {
    const root = document.getElementById('talk-to-us');
    if (!root) return;

    initReveals();
    initMap();
    initCopyAddress();
    initSnapScroll(root);
    window.HomeContatoDropdown?.init();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
